import React, { useState, useCallback, useRef, useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const XLSX = require('xlsx');

interface Question {
  id: number;
  year: string;
  round: string;
  number: string | number;
  subject: string;
  question: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  answer: string;
  explanation: string;
  [key: string]: any;
}

interface ImgInfo {
  field: string;   // 'question' | 'option1' ~ 'option4'
  path: string;
  originalScale: number;
  currentScale: number;
}

// 텍스트에서 [N:img:경로] 또는 [Nimg:경로] 파싱
const parseImages = (text: string): ImgInfo[] => {
  if (!text) return [];
  const results: ImgInfo[] = [];
  const regex = /\[(\d+):img:([^\]]+)\]|\[(\d+)img:([^\]]+)\]/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const scale = Number(m[1] || m[3]);
    const path = (m[2] || m[4]).trim();
    results.push({ field: '', path, originalScale: scale, currentScale: scale });
  }
  return results;
};

// 텍스트에서 이미지 크기 교체
const replaceImageScale = (text: string, path: string, newScale: number): string => {
  if (!text) return text;
  text = text.replace(
    new RegExp(`\\[(\\d+):img:${path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g'),
    `[${newScale}:img:${path}]`
  );
  text = text.replace(
    new RegExp(`\\[(\\d+)img:${path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g'),
    `[${newScale}img:${path}]`
  );
  return text;
};

// 색상 맵 (QuestionView와 동일)
const COLOR_MAP: Record<string, string> = {
  '빨강': '#e74c3c', '빨간': '#e74c3c', '빨간색': '#e74c3c',
  '파랑': '#3498db', '파란': '#3498db', '파란색': '#3498db',
  '초록': '#27ae60', '초록색': '#27ae60', '녹색': '#27ae60',
  '주황': '#e67e22', '주황색': '#e67e22', '오렌지': '#e67e22',
  '보라': '#9b59b6', '보라색': '#9b59b6',
  '노랑': '#f1c40f', '노란': '#f1c40f', '노란색': '#f1c40f',
  '분홍': '#e91e63', '분홍색': '#e91e63', '핑크': '#e91e63',
  '갈색': '#8b4513', '회색': '#7f8c8d', '그레이': '#7f8c8d',
  '검정': '#000000', '검정색': '#000000', '검은': '#000000',
  'red': '#e74c3c', 'blue': '#3498db', 'green': '#27ae60',
  'orange': '#e67e22', 'purple': '#9b59b6', 'yellow': '#f1c40f',
  'pink': '#e91e63', 'brown': '#8b4513', 'gray': '#7f8c8d',
  'grey': '#7f8c8d', 'black': '#000000',
};

// QuestionView의 renderText와 동일한 방식 - 인라인 렌더링
const renderPreview = (text: string, changes: Record<string, number>): React.ReactNode => {
  if (!text) return null;
  let cleaned = text.replace(/_x000d_/gi, '').replace(/\r/g, '');
  cleaned = cleaned.replace(/\[([^\]]+)\]([^[]*)\[\/\]/g, (match, color, content) => {
    const code = COLOR_MAP[color.toLowerCase()] || COLOR_MAP[color];
    if (code) return `@@COLOR:${code}@@${content}@@/COLOR@@`;
    return match;
  });
  const brPattern = new RegExp('(\\[\\d+:img:[^\\]]+\\]|\\[\\d+img:[^\\]]+\\]|<br\\s*\\/?>|@@COLOR:[^@]+@@[^@]*@@\\/COLOR@@)', 'gi');
  const parts = cleaned.split(brPattern);
  return (
    <>
      {parts.map((part, i) => {
        // 이미지 - changes에서 크기 override, 선택된 이미지는 파란 테두리 표시
        const imgMatch = part.match(/^\[(\d+):img:(.+)\]$/) || part.match(/^\[(\d+)img:(.+)\]$/);
        if (imgMatch) {
          const path = imgMatch[2].trim();
          const scale = changes[path] ?? Number(imgMatch[1]);
          const src = `/images/${path}`;
          return (
            <React.Fragment key={i}>
              <img src={src} alt=""
                style={{ width: `${scale}%`, maxWidth: '100%', borderRadius: 4, verticalAlign: 'middle', border: changes[path] ? '2px solid #3498db' : 'none' }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
              <span style={{ fontSize: 10, color: '#e67e22', fontWeight: 700, verticalAlign: 'super', marginLeft: 1 }}>{scale}%</span>
            </React.Fragment>
          );
        }
        // <br>
        if (/^<br\s*\/?>$/i.test(part)) return <br key={i} />;
        // 색상 태그
        const colorMatch = part.match(/^@@COLOR:([^@]+)@@(.*)@@\/COLOR@@$/);
        if (colorMatch) return <span key={i} style={{ color: colorMatch[1] }}>{colorMatch[2]}</span>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

const AdminImageEditor: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [rawRows, setRawRows] = useState<any[][]>([]);
  const [fileName, setFileName] = useState('');
  const [curIdx, setCurIdx] = useState(0);
  const [filterYear, setFilterYear] = useState('');
  const [filterRound, setFilterRound] = useState('');
  const [changes, setChanges] = useState<Record<string, number>>({});
  const [selectedImg, setSelectedImg] = useState<ImgInfo | null>(null);
  const [inputSize, setInputSize] = useState(80);
  const [loaded, setLoaded] = useState(false);
  const [fontSize, setFontSize] = useState(17);
  const fileHandleRef = useRef<any>(null);
  const editorListRef = useRef<HTMLDivElement>(null);
  const filteredLengthRef = useRef(0);
  const editorIsFirstRender = useRef(true);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        if (tag === 'BUTTON') (e.target as HTMLElement).blur();
        if (e.key === 'ArrowRight') {
          setCurIdx(i => Math.min(filteredLengthRef.current - 1, i + 1));
        } else {
          setCurIdx(i => Math.max(0, i - 1));
        }
        setSelectedImg(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, []);

  // ✅ 사이드바 중앙 고정
  useEffect(() => {
    if (editorIsFirstRender.current) { editorIsFirstRender.current = false; return; }
    if (!editorListRef.current) return;
    const container = editorListRef.current;
    const items = container.querySelectorAll('div[style]');
    const activeEl = items[curIdx] as HTMLElement;
    if (!activeEl) return;
    const containerH = container.clientHeight;
    const itemTop = activeEl.offsetTop - container.offsetTop;
    const itemH = activeEl.clientHeight;
    container.scrollTop = Math.max(0, itemTop - containerH / 2 + itemH / 2);
  }, [curIdx]);

  const years = Array.from(new Set(questions.map(q => q.year))).filter(Boolean).sort();
  const rounds = Array.from(new Set(questions.map(q => q.round))).filter(Boolean).sort();

  const filtered = questions.filter(q => {
    if (filterYear && q.year !== filterYear) return false;
    if (filterRound && q.round !== filterRound) return false;
    return true;
  });

  // ✅ filtered 길이 ref 업데이트
  filteredLengthRef.current = filtered.length;

  const q = filtered[curIdx];

  // 현재 문제의 모든 이미지 수집
  const getCurrentImages = useCallback((): ImgInfo[] => {
    if (!q) return [];
    const fields = ['question', 'option1', 'option2', 'option3', 'option4', 'explanation'];
    const imgs: ImgInfo[] = [];
    fields.forEach(field => {
      const text = q[field] || '';
      parseImages(text).forEach(img => {
        imgs.push({ ...img, field, currentScale: changes[img.path] ?? img.originalScale });
      });
    });
    return imgs;
  }, [q, changes]);

  // 엑셀 파싱 공통 함수
  const parseExcel = (buffer: ArrayBuffer) => {
    const data = new Uint8Array(buffer);
    const wb = XLSX.read(data, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (rows.length < 2) { alert('데이터가 없습니다.'); return; }

    const hdrs = rows[0].map((h: any) => String(h || '').trim());
    setRawRows(rows);

    const getCol = (row: any[], ...names: string[]) => {
      for (const name of names) {
        const idx = hdrs.indexOf(name);
        if (idx >= 0 && row[idx] != null) return String(row[idx]).trim();
      }
      return '';
    };
    const getNum = (row: any[], ...names: string[]) => {
      const v = getCol(row, ...names);
      const n = parseFloat(v);
      return isNaN(n) ? v : String(Math.round(n));
    };

    const qs: Question[] = rows.slice(1).filter(r => r.some(Boolean)).map((row, idx) => ({
      id: idx,
      year: getNum(row, '년도'),
      round: getNum(row, '차수'),
      number: getCol(row, '번호'),
      subject: getCol(row, '과목'),
      question: getCol(row, '문제'),
      option1: getCol(row, '보기1'),
      option2: getCol(row, '보기2'),
      option3: getCol(row, '보기3'),
      option4: getCol(row, '보기4'),
      answer: getCol(row, '정답'),
      explanation: getCol(row, '해설', '설명'),
    }));

    setQuestions(qs);
    setChanges({});
    setSelectedImg(null);
    setCurIdx(0);
    setFilterYear('');
    setFilterRound('');
    setLoaded(true);
  };

  // 엑셀 로드 - File System Access API 지원 시 파일 핸들 저장
  const handleLoad = async () => {
    try {
      // File System Access API 지원 여부 확인 (Chrome/Edge)
      if ((window as any).showOpenFilePicker) {
        const [handle] = await (window as any).showOpenFilePicker({
          types: [{ description: 'Excel Files', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx', '.xls'] } }],
          multiple: false,
        });
        fileHandleRef.current = handle;
        const file = await handle.getFile();
        setFileName(file.name);
        const buffer = await file.arrayBuffer();
        parseExcel(buffer);
      } else {
        // 미지원 브라우저 - 기존 input 방식 fallback
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.xlsx,.xls';
        input.onchange = async (e: any) => {
          const file = e.target.files?.[0];
          if (!file) return;
          fileHandleRef.current = null; // 핸들 없음
          setFileName(file.name);
          const buffer = await file.arrayBuffer();
          parseExcel(buffer);
        };
        input.click();
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') alert('파일 열기 실패: ' + err.message);
    }
  };

  // 이미지 선택
  const handleSelectImg = (img: ImgInfo) => {
    setSelectedImg(img);
    setInputSize(changes[img.path] ?? img.currentScale);
  };

  // 크기 변경
  const applySize = (size: number) => {
    if (!selectedImg) return;
    const newSize = Math.max(1, Math.min(500, size));
    setInputSize(newSize);
    setChanges(prev => ({ ...prev, [selectedImg.path]: newSize }));
    setSelectedImg(prev => prev ? { ...prev, currentScale: newSize } : null);
  };

  // 현재 inputSize를 ref로 추적 (클로저 stale 문제 방지)
  const inputSizeRef = useRef(inputSize);
  useEffect(() => { inputSizeRef.current = inputSize; }, [inputSize]);

  // ▼▲ 버튼 long press - 누르고 있으면 자동 증감
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPress = (direction: 1 | -1) => {
    // 즉시 1회 실행 (1씩)
    applySize(inputSizeRef.current + direction * 1);
    // 600ms 후 반복 시작 (1씩 자동 증감)
    pressTimer.current = setTimeout(() => {
      pressInterval.current = setInterval(() => {
        setInputSize(prev => {
          const next = Math.max(1, Math.min(500, prev + direction * 1));
          setChanges(ch => selectedImg ? { ...ch, [selectedImg.path]: next } : ch);
          setSelectedImg(prev2 => prev2 ? { ...prev2, currentScale: next } : null);
          inputSizeRef.current = next;
          return next;
        });
      }, 80);
    }, 600);
  };

  const stopPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    if (pressInterval.current) clearInterval(pressInterval.current);
    pressTimer.current = null;
    pressInterval.current = null;
  };

  // 엑셀 저장 - File System Access API 지원 시 원본 위치에 바로 저장
  const handleSave = async () => {
    if (Object.keys(changes).length === 0) { alert('변경된 이미지가 없습니다.'); return; }

    // rawRows 복사 후 변경사항 적용
    const newRows = rawRows.map((row, ri) => {
      if (ri === 0) return row;
      return row.map((cell: any) => {
        if (typeof cell !== 'string') return cell;
        let updated = cell;
        Object.entries(changes).forEach(([path, scale]) => {
          updated = replaceImageScale(updated, path, scale);
        });
        return updated;
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(newRows);
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // ✅ confirm 제거 - user activation 유지하여 File System Access API 정상 동작
    if (fileHandleRef.current) {
      try {
        const writable = await fileHandleRef.current.createWritable();
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        await writable.write(new Blob([wbout], { type: 'application/octet-stream' }));
        await writable.close();
        alert(`✅ 저장 완료! (원본 파일 덮어쓰기)\n변경 ${Object.keys(changes).length}개 적용\n${fileName}`);
        return;
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        alert('원본 저장 실패, 다운로드로 대체합니다.\n' + err.message);
      }
    }

    // fallback - 다운로드 폴더로 저장
    XLSX.writeFile(wb, fileName);
    alert(`저장 완료! (다운로드 폴더)\n${fileName}`);
  };

  const currentImages = getCurrentImages();
  const changeCount = Object.keys(changes).length;

  if (!loaded) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <div className="admin-page-title"><span className="icon">🖼️</span>이미지 크기 에디터</div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🖼️</div>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 14 }}>
              문제 엑셀 파일을 불러와서 이미지 크기를 조절하세요.<br />
              Chrome/Edge에서는 원본 폴더에 바로 저장됩니다.
            </p>
            <button className="btn btn-primary" style={{ cursor: 'pointer', fontSize: 15, padding: '12px 32px' }}
              onClick={handleLoad}>
              📂 엑셀 파일 선택
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ padding: 0, height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>

      {/* ✅ 상단 툴바 - 한 줄 compact */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 12px',
        background: 'white',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
        minHeight: 38,
      }}>
        {/* 타이틀 */}
        <span style={{ fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap' }}>🖼️ 이미지 에디터</span>

        {/* 구분선 */}
        <span style={{ color: 'var(--border)', fontSize: 16 }}>|</span>

        {/* 파일명 */}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {fileName}
        </span>

        {/* 구분선 */}
        <span style={{ color: 'var(--border)', fontSize: 16 }}>|</span>

        {/* 년도 선택 */}
        <select
          className="form-select"
          style={{ height: 26, fontSize: 11, padding: '1px 4px', width: 80 }}
          value={filterYear}
          onChange={e => { setFilterYear(e.target.value); setCurIdx(0); }}
        >
          <option value="">년도 전체</option>
          {years.map(y => <option key={y} value={y}>{y}년</option>)}
        </select>

        {/* 차수 선택 */}
        <select
          className="form-select"
          style={{ height: 26, fontSize: 11, padding: '1px 4px', width: 80 }}
          value={filterRound}
          onChange={e => { setFilterRound(e.target.value); setCurIdx(0); }}
        >
          <option value="">차수 전체</option>
          {rounds.map(r => <option key={r} value={r}>{r}차</option>)}
        </select>

        {/* 문제 수 */}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filtered.length}문제
        </span>

        {/* 구분선 */}
        <span style={{ color: 'var(--border)', fontSize: 16 }}>|</span>

        {/* 폰트 크기 슬라이더 */}
        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fontSize}px</span>
        <input type="range" min={12} max={28} value={fontSize}
          onChange={e => setFontSize(Number(e.target.value))}
          style={{ width: 80, cursor: 'pointer' }}
        />

        {/* 오른쪽 버튼 영역 */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {changeCount > 0 && (
            <span style={{
              fontSize: 11,
              background: '#fef3c7',
              color: '#d97706',
              padding: '2px 8px',
              borderRadius: 20,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}>
              변경 {changeCount}개
            </span>
          )}
          <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer', fontSize: 11, padding: '3px 8px', height: 26, lineHeight: '20px' }}
            onClick={handleLoad}>
            📂 다른 파일
          </label>
          <button
            className="btn btn-primary btn-sm"
            style={{ fontSize: 11, padding: '3px 10px', height: 26, lineHeight: '20px' }}
            onClick={handleSave}
          >
            💾 엑셀 저장
          </button>
        </div>
      </div>

      {/* 본문 3분할 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* 1. 문제 목록 */}
        <div style={{ width: 200, background: '#f8fafc', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', padding: '6px 10px', borderBottom: '1px solid var(--border)', fontWeight: 700 }}>
            문제 목록
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }} ref={editorListRef}>
            {filtered.map((fq, i) => {
              const hasImg = parseImages(fq.question + fq.option1 + fq.option2 + fq.option3 + fq.option4).length > 0;
              return (
                <div key={fq.id} onClick={() => { setCurIdx(i); setSelectedImg(null); }}
                  style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid var(--border)',
                    background: i === curIdx ? 'var(--primary)' : 'white',
                    color: i === curIdx ? 'white' : 'var(--text-secondary)' }}>
                  [{fq.year}-{fq.round}] {fq.number}번
                  {hasImg && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.8 }}>🖼️</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* 2. 문제 내용 - ✅ 문제풀이창(850px)과 동일한 너비로 고정 */}
        <div style={{ width: 850, minWidth: 850, maxWidth: 850, overflow: 'auto', padding: 16, background: 'white', boxSizing: 'border-box' }}>
          {q ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                [{q.year}년 {q.round}차] {q.subject} {q.number}번
              </div>
              {/* ✅ 문제풀이창 padding(16px*2=32px) 고려해서 내부 콘텐츠 너비 맞춤 */}
              <div style={{ fontSize, lineHeight: 2, marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                {renderPreview(q.question, changes)}
              </div>
              {['option1','option2','option3','option4'].map((field, n) => {
                const text = q[field];
                if (!text) return null;
                return (
                  <div key={field} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: fontSize + 2, flexShrink: 0 }}>{['①','②','③','④'][n]}</span>
                    <div style={{ flex: 1, fontSize, lineHeight: 2 }}>{renderPreview(text, changes)}</div>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>문제를 선택해주세요</div>
          )}
        </div>

        {/* 3. 이미지 크기 조절 패널 */}
        <div style={{ width: 240, background: 'white', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13 }}>
            🖼️ 이미지 크기 조절
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            {currentImages.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                이 문제에 이미지가 없습니다
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>이미지 클릭하여 선택</div>
                {currentImages.map((img, i) => {
                  const isSelected = selectedImg?.path === img.path;
                  const scale = changes[img.path] ?? img.originalScale;
                  const src = `/images/${img.path}`;
                  return (
                    <div key={i} onClick={() => handleSelectImg({ ...img, currentScale: scale })}
                      style={{ marginBottom: 10, padding: 8, border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', background: isSelected ? 'var(--primary-light)' : 'white' }}>
                      <img src={src} alt="" style={{ width: '100%', maxHeight: 80, objectFit: 'contain', borderRadius: 4 }}
                        onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, wordBreak: 'break-all' }}>{img.path}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isSelected ? 'var(--primary)' : 'var(--text-secondary)' }}>
                        {scale}% {changes[img.path] && changes[img.path] !== img.originalScale ? `(원본: ${img.originalScale}%)` : ''}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* 크기 조절 컨트롤 */}
          {selectedImg && (
            <div style={{ padding: 12, borderTop: '1px solid var(--border)', flexShrink: 0 }}>

              {/* 타이틀 */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textAlign: 'center' }}>
                크기 조절 (width %)
              </div>

              {/* ① 누르고 있으면 자동 증감 (long press) ▼▲ */}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 4 }}>
                꾹 누르면 자동 증감
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 10 }}>
                <button
                  onMouseDown={() => startPress(-1)}
                  onMouseUp={stopPress}
                  onMouseLeave={stopPress}
                  onTouchStart={() => startPress(-1)}
                  onTouchEnd={stopPress}
                  style={{ width: 40, height: 40, background: '#e74c3c', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 18, fontWeight: 700, userSelect: 'none' }}>
                  ▼
                </button>
                <input
                  type="number" value={inputSize} min={1} max={500}
                  onChange={e => setInputSize(Number(e.target.value))}
                  onKeyDown={e => e.key === 'Enter' && applySize(inputSize)}
                  style={{ width: 64, height: 40, textAlign: 'center', fontSize: 18, fontWeight: 700, border: '2px solid var(--primary)', borderRadius: 8 }}
                />
                <button
                  onMouseDown={() => startPress(1)}
                  onMouseUp={stopPress}
                  onMouseLeave={stopPress}
                  onTouchStart={() => startPress(1)}
                  onTouchEnd={stopPress}
                  style={{ width: 40, height: 40, background: '#2ecc71', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: 18, fontWeight: 700, userSelect: 'none' }}>
                  ▲
                </button>
              </div>

              {/* ② 클릭당 5씩 증감 버튼 */}
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 6 }}>
                클릭당 5씩 증감
              </div>
              <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
                {[-20, -10, -5].map(v => (
                  <button key={v}
                    onClick={() => applySize(inputSizeRef.current + v)}
                    style={{ flex: 1, height: 30, background: '#fdecea', color: '#e74c3c', border: '1px solid #f5c6c2', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                    {v}
                  </button>
                ))}
                {[+5, +10, +20].map(v => (
                  <button key={v}
                    onClick={() => applySize(inputSizeRef.current + v)}
                    style={{ flex: 1, height: 30, background: '#e8f8f0', color: '#27ae60', border: '1px solid #b7e4cc', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                    +{v}
                  </button>
                ))}
              </div>

              {/* 초기화 */}
              <button className="btn btn-ghost btn-sm" style={{ width: '100%' }} onClick={() => applySize(selectedImg.originalScale)}>
                ↩ 원본 크기로 초기화 ({selectedImg.originalScale}%)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminImageEditor;
